import { useEffect, useRef, useState, useId } from 'react';
import { Pencil, Check, AlertCircle, AlertTriangle, RotateCcw } from 'lucide-react';
import { useTenant } from '../../context/TenantContext.jsx';
import { useEditMode } from '../../context/EditModeContext.jsx';
import {
  PRODUCT_EDIT_TEXTS,
  DESCRIPTION_MAX,
  countCodepoints,
  normalizeClientText,
  containsHtmlTags,
  mapProductEditError,
  getAdminProductDisplay,
  updateAdminProductDisplay,
} from '../../lib/product-display.js';

/**
 * EditableProductText — Inline editing for product name and description (Paket P).
 *
 * Adheres strictly to SPEC-P.md (§10, §11, §12, §13):
 * - Storefront-First inline editing for authenticated tenant_admins
 * - Single-line for name (Enter commits, Esc cancels, max 80 codepoints)
 * - Multiline for description (Ctrl/Cmd+Enter commits, Esc cancels, max 5.000 codepoints, pre-line)
 * - Two-step reset ("Auf Standard zurücksetzen" -> "Wirklich zurücksetzen?" -> "Ja, zurücksetzen")
 * - Error mapping to stable German strings; raw server errors are never shown
 * - Draft persistence across SPA navigation via EditModeContext
 */
export function EditableProductText({
  productId,
  value = '',
  onUpdate,
  className = '',
  tag: Tag = 'span',
  children,
  ...props
}) {
  let tenant = null;
  try {
    tenant = useTenant()?.tenant || null;
  } catch {
    tenant = null;
  }

  const {
    canEdit,
    editMode,
    bumpRevision,
    recheckSession,
    sessionExpired,
    notifySessionExpired,
    setUnsavedEdit,
    productDrafts,
    setProductDraft,
    clearProductDraft,
  } = useEditMode();

  // REV 2 (§3a, §11 E4): nur Beschreibung. Der Produktname ist global und nicht editierbar.
  const maxLen = DESCRIPTION_MAX;
  const draftKey = `${tenant?.id || 'default'}:${productId}:description`;

  const label = PRODUCT_EDIT_TEXTS.LABEL_DESCRIPTION;
  const editTooltip = PRODUCT_EDIT_TEXTS.EDIT_TOOLTIP_DESCRIPTION;
  const placeholder = PRODUCT_EDIT_TEXTS.PLACEHOLDER_DESCRIPTION;
  const keyHint = PRODUCT_EDIT_TEXTS.KEY_HINT_DESCRIPTION;

  // Check if there is an unsaved draft from a previous SPA navigation
  const existingDraft = productDrafts?.[draftKey];
  const [editing, setEditing] = useState(Boolean(existingDraft?.isOpen));
  const [draft, setDraft] = useState(existingDraft ? existingDraft.value : (value || ''));

  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [resetFlash, setResetFlash] = useState(false);
  const [error, setError] = useState('');

  const [adminData, setAdminData] = useState(null);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [adminLoadError, setAdminLoadError] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const cancelledRef = useRef(false);
  const inputId = useId();

  // Sync draft value if parent value changes and not actively editing
  useEffect(() => {
    if (!editing && !existingDraft) {
      setDraft(value || '');
    }
  }, [value, editing, existingDraft]);

  // Focus and select input on edit mode open
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (typeof inputRef.current.select === 'function') {
        inputRef.current.select();
      }
    }
  }, [editing]);

  // Lazy-load admin product info (original text, isOverridden status) when opening editor
  const fetchAdminDetails = async () => {
    if (!productId || !tenant?.id) return;
    setLoadingAdmin(true);
    setAdminLoadError(null);
    try {
      const data = await getAdminProductDisplay(productId, tenant.id);
      setAdminData(data);
    } catch (err) {
      setAdminLoadError(PRODUCT_EDIT_TEXTS.ERR_ORIGINAL_UNAVAILABLE);
    } finally {
      setLoadingAdmin(false);
    }
  };

  const isOverridden = Boolean(adminData?.description?.isOverridden);
  const originalText = adminData?.description?.original;

  // Not editable for anonymous or when edit mode is toggled off
  if (!canEdit || !editMode) {
    return (
      <Tag className={className} {...props}>
        {children ?? value}
      </Tag>
    );
  }

  const beginEdit = () => {
    cancelledRef.current = false;
    setDraft(value || '');
    setError('');
    setConfirmReset(false);
    setEditing(true);
    setProductDraft(draftKey, { value: value || '', isOpen: true });
    fetchAdminDetails();
  };

  const cancelEdit = () => {
    cancelledRef.current = true;
    setEditing(false);
    setConfirmReset(false);
    setError('');
    clearProductDraft(draftKey);
    setUnsavedEdit?.(draftKey, false);
    setDraft(value || '');
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  const handleDraftChange = (newVal) => {
    setDraft(newVal);
    setError('');
    setProductDraft(draftKey, { value: newVal, isOpen: true });
    setUnsavedEdit?.(draftKey, newVal !== (value || ''));
  };

  const commit = async () => {
    if (cancelledRef.current || saving) return;

    const currentNormalized = normalizeClientText('description', value);
    const draftNormalized = normalizeClientText('description', draft);

    // If text contains HTML tags (and not mathematical/comparison notation)
    if (draft && containsHtmlTags(draft)) {
      setError(PRODUCT_EDIT_TEXTS.ERR_HTML_NOT_ALLOWED);
      return;
    }

    const codepointCount = countCodepoints(draft);
    if (codepointCount > maxLen) {
      setError(PRODUCT_EDIT_TEXTS.ERR_DESCRIPTION_TOO_LONG);
      return;
    }

    // If unchanged, close without API request
    if (draftNormalized === currentNormalized) {
      setEditing(false);
      clearProductDraft(draftKey);
      setUnsavedEdit?.(draftKey, false);
      if (triggerRef.current) {
        triggerRef.current.focus();
      }
      return;
    }

    setSaving(true);
    setError('');

    const payload = { description: draftNormalized };

    try {
      const res = await updateAdminProductDisplay(productId, tenant?.id, payload);
      setSaving(false);
      setEditing(false);
      clearProductDraft(draftKey);
      setUnsavedEdit?.(draftKey, false);
      setAdminData(res);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      bumpRevision();

      if (onUpdate) {
        onUpdate(res);
      }

      if (triggerRef.current) {
        triggerRef.current.focus();
      }
    } catch (err) {
      setSaving(false);
      const mapped = mapProductEditError(err);
      setError(mapped);
      if (err?.status === 401 || err?.statusCode === 401) {
        notifySessionExpired?.(true);
        recheckSession?.();
      }
    }
  };

  const resetToStandard = async () => {
    if (!confirmReset) {
      setConfirmReset(true);
      return;
    }

    setSaving(true);
    setError('');

    const payload = { description: null };

    try {
      const res = await updateAdminProductDisplay(productId, tenant?.id, payload);
      setSaving(false);
      setEditing(false);
      setConfirmReset(false);
      clearProductDraft(draftKey);
      setUnsavedEdit?.(draftKey, false);
      setAdminData(res);
      setResetFlash(true);
      setTimeout(() => setResetFlash(false), 2000);
      bumpRevision();

      if (onUpdate) {
        onUpdate(res);
      }

      if (triggerRef.current) {
        triggerRef.current.focus();
      }
    } catch (err) {
      setSaving(false);
      const mapped = mapProductEditError(err);
      setError(mapped);
      if (err?.status === 401 || err?.statusCode === 401) {
        notifySessionExpired?.(true);
        recheckSession?.();
      }
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  const handleContainerBlur = (e) => {
    if (cancelledRef.current || saving) return;
    if (containerRef.current && containerRef.current.contains(e.relatedTarget)) {
      return;
    }
    commit();
  };

  const codepointCount = countCodepoints(draft);
  const isOverLimit = codepointCount > maxLen;
  const counterText = PRODUCT_EDIT_TEXTS.COUNTER_DESCRIPTION(codepointCount);

  return (
    <Tag className={`relative inline-block w-full group ${className}`} {...props}>
      {/* Trigger button/surface in editMode */}
      <span
        ref={triggerRef}
        role="button"
        tabIndex={0}
        onClick={beginEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            beginEdit();
          }
        }}
        aria-label={editTooltip}
        title={editTooltip}
        data-testid="editable-product-description"
        className={`min-h-[44px] cursor-text rounded-md outline-dashed outline-1 outline-offset-4 transition-colors hover:bg-[var(--primary)]/5 p-1 flex flex-wrap items-center gap-2 ${
          savedFlash
            ? 'outline-emerald-500 bg-emerald-50 text-emerald-900'
            : (resetFlash ? 'outline-blue-500 bg-blue-50' : 'outline-slate-400/80')
        } ${saving ? 'opacity-50' : ''}`}
      >
        <span className="flex-1 min-w-0 break-words">
          {children ?? (value || <span className="italic text-slate-400 font-normal text-sm">{placeholder}</span>)}
        </span>

        <span className="inline-flex items-center gap-1 shrink-0 text-slate-400 group-hover:text-[var(--primary)] transition-colors">
          {isOverridden && (
            <span
              className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-xs font-bold text-amber-800"
              title={PRODUCT_EDIT_TEXTS.OVERRIDE_BADGE_TOOLTIP}
            >
              {PRODUCT_EDIT_TEXTS.OVERRIDE_BADGE}
            </span>
          )}
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </span>

        {saving && (
          <span className="text-xs text-slate-500 font-normal">{PRODUCT_EDIT_TEXTS.STATE_SAVING}</span>
        )}
      </span>

      {/* Screen Reader Live Announcements */}
      {savedFlash && (
        <span role="status" aria-live="polite" className="sr-only">
          {PRODUCT_EDIT_TEXTS.STATE_SAVED}
        </span>
      )}
      {resetFlash && (
        <span role="status" aria-live="polite" className="sr-only">
          {PRODUCT_EDIT_TEXTS.STATE_RESET}
        </span>
      )}

      {/* Inline Editor Flyout */}
      {editing && (
        <div
          ref={containerRef}
          onBlur={handleContainerBlur}
          role="group"
          aria-labelledby={`${inputId}-label`}
          className="absolute left-0 top-full z-50 mt-2 flex w-full min-w-[320px] max-w-2xl flex-col gap-2.5 rounded-2xl border border-[var(--primary)]/40 bg-white p-4 shadow-2xl text-[var(--foreground)]"
        >
          {sessionExpired && (
            <div
              role="alert"
              data-testid="session-expired-product-alert"
              className="flex items-center gap-1.5 rounded-xl bg-amber-50 p-2.5 text-xs text-amber-800 border border-amber-300 font-medium"
            >
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
              <span>Sitzung ist abgelaufen. Bitte vor dem Speichern neu anmelden.</span>
            </div>
          )}

          {/* Header with field label & counter */}
          <div className="flex items-center justify-between gap-2 border-b border-[var(--border)] pb-2 text-xs">
            <label id={`${inputId}-label`} htmlFor={inputId} className="font-bold text-[var(--foreground)]">
              {label}
            </label>
            <span
              aria-live="polite"
              className={`font-mono text-xs ${isOverLimit ? 'text-red-600 font-bold' : 'text-slate-500'}`}
            >
              {counterText}
            </span>
          </div>

          {/* Form input: single-line input or multi-line textarea */}
            <textarea
              id={inputId}
              ref={inputRef}
              rows={6}
              value={draft}
              disabled={saving}
              onChange={(e) => handleDraftChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              aria-busy={saving ? 'true' : 'false'}
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2 text-base text-[var(--foreground)] outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 resize-y min-h-[140px] max-h-[320px]"
            />

          {/* Helper hint */}
          <p className="text-xs text-[var(--secondary)]">
            {keyHint}
          </p>

          {/* Original Info Section */}
          <div className="rounded-lg bg-[var(--surface-container-low,#f8fafc)] p-2.5 text-xs text-[var(--secondary)] space-y-1.5">
            {loadingAdmin ? (
              <p className="italic text-slate-400">{PRODUCT_EDIT_TEXTS.STATE_LOADING}</p>
            ) : adminLoadError ? (
              <p className="text-amber-700">{adminLoadError}</p>
            ) : originalText ? (
              <div>
                  <div>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setShowOriginal((v) => !v)}
                      className="font-semibold text-[var(--primary)] hover:underline cursor-pointer"
                    >
                      {showOriginal ? PRODUCT_EDIT_TEXTS.ORIGINAL_HIDE : PRODUCT_EDIT_TEXTS.ORIGINAL_SHOW}
                    </button>
                    {showOriginal && (
                      <p className="mt-1.5 whitespace-pre-line text-xs text-slate-600 border-t border-slate-200 pt-1.5">
                        {originalText}
                      </p>
                    )}
                  </div>
              </div>
            ) : (
              <p className="italic text-slate-400">Kein Originaltext hinterlegt.</p>
            )}

            {/* Reset to standard button / confirmation */}
            {isOverridden && !loadingAdmin && !adminLoadError && (
              <div className="border-t border-slate-200/80 pt-1.5 mt-1">
                {confirmReset ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-700">{PRODUCT_EDIT_TEXTS.BTN_RESET_CONFIRM_TITLE}</span>
                    <button
                      type="button"
                      disabled={saving}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={resetToStandard}
                      className="rounded bg-red-600 px-2 py-1 text-xs font-bold text-white hover:bg-red-700 cursor-pointer"
                    >
                      {PRODUCT_EDIT_TEXTS.BTN_RESET_CONFIRM_YES}
                    </button>
                    <button
                      type="button"
                      disabled={saving}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setConfirmReset(false)}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer"
                    >
                      {PRODUCT_EDIT_TEXTS.BTN_CANCEL}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={saving}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setConfirmReset(true)}
                    className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:text-[var(--primary)] hover:underline cursor-pointer"
                  >
                    <RotateCcw className="h-3 w-3" />
                    <span>{PRODUCT_EDIT_TEXTS.BTN_RESET}</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Action buttons (Speichern / Abbrechen) */}
          <div className="flex flex-wrap items-center justify-end gap-2 pt-1 border-t border-[var(--border)]">
            <button
              type="button"
              disabled={saving}
              onMouseDown={(e) => e.preventDefault()}
              onClick={cancelEdit}
              className="min-h-[44px] rounded-xl border border-[var(--border)] px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 cursor-pointer transition-colors"
            >
              {PRODUCT_EDIT_TEXTS.BTN_CANCEL}
            </button>
            <button
              type="button"
              disabled={saving || isOverLimit}
              onMouseDown={(e) => e.preventDefault()}
              onClick={commit}
              className={`min-h-[44px] rounded-xl bg-[var(--primary)] px-5 py-2 text-xs font-bold text-white shadow-sm transition-opacity ${
                saving || isOverLimit ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90 cursor-pointer'
              }`}
            >
              {saving ? PRODUCT_EDIT_TEXTS.STATE_SAVING : PRODUCT_EDIT_TEXTS.BTN_SAVE}
            </button>
          </div>

          {/* Error Alert Box */}
          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-xl bg-red-50 p-2.5 text-xs text-red-700 border border-red-200"
            >
              <AlertCircle className="h-4 w-4 shrink-0 text-red-600 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}
    </Tag>
  );
}
